import { Injectable, computed, signal } from '@angular/core';
import { HdAnnotation, HdAnnotationTool } from './hd-annotation.models';

const HISTORY_LIMIT = 50;

/**
 * Eén globale annotatie-state, geprovide op page-niveau (`chart-environment-
 * full-page`). Omdat de page-component bij een tab-wissel niet vernietigd wordt
 * blijft deze laag vanzelf staan over tabs en chart-wissels heen; enkel `clear`
 * wist hem (zie spec §10).
 *
 * Undo werkt via snapshots: discrete acties (`add`/`clear`) snapshotten zelf;
 * voor sleep-gebaren (gom, verplaats) roept de laag éénmaal `beginChange()` aan
 * bij pointerdown en muteert daarna met de `*Raw`-methodes, zodat het hele
 * gebaar in één keer ge-undood wordt.
 */
@Injectable()
export class HdAnnotationStore {
  private seq = 0;
  private history: HdAnnotation[][] = [];

  readonly annotations = signal<readonly HdAnnotation[]>([]);
  readonly tool = signal<HdAnnotationTool>('none');
  readonly color = signal<string>('#f59e0b');
  readonly width = signal<number>(10);
  readonly opacity = signal<number>(0.5);
  readonly selectedId = signal<string | null>(null);

  readonly active = computed(() => this.tool() !== 'none');

  nextId(): string {
    return 'anno-' + ++this.seq;
  }

  beginChange(): void {
    this.history.push([...this.annotations()]);
    if (this.history.length > HISTORY_LIMIT) {
      this.history.shift();
    }
  }

  addRaw(a: HdAnnotation): void {
    this.annotations.update(list => [...list, a]);
  }

  removeRaw(id: string): void {
    this.annotations.update(list => list.filter(a => a.id !== id));
  }

  replaceRaw(id: string, next: HdAnnotation): void {
    this.annotations.update(list => list.map(a => (a.id === id ? next : a)));
  }

  add(a: HdAnnotation): void {
    this.beginChange();
    this.addRaw(a);
  }

  clear(): void {
    this.beginChange();
    this.annotations.set([]);
    this.selectedId.set(null);
  }

  undo(): void {
    const prev = this.history.pop();
    if (prev) {
      this.annotations.set(prev);
      this.selectedId.set(null);
    }
  }

  setTool(tool: HdAnnotationTool): void {
    this.tool.set(tool);
    if (tool !== 'move') {
      this.selectedId.set(null);
    }
  }

  toggleTool(tool: HdAnnotationTool): void {
    this.setTool(this.tool() === tool ? 'none' : tool);
  }
}
